
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { FlatTreeControl } from '@angular/cdk/tree';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeModule,
} from '@angular/material/tree';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CerrixTreeComponent } from '../cerrix-tree/cerrix-tree.component';
import { TreeItemFlatNode } from '../models/tree/tree-item-flat-node.model';
import { TreeItemNode } from '../models/tree/tree-item-node.model';
import { TreeNestedNode } from '../models/tree/tree-nested-node.model';
import { ErrorMessagesPipe } from '../pipes/error-messages.pipe';
import { BaseTreeService } from '../services/base-tree.service';

@Component({
    selector: 'cerrix-tree-select',
    imports: [
    ErrorMessagesPipe,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDialogModule,
    FormsModule,
    CerrixTreeComponent,
    MatTreeModule,
    FontAwesomeModule,
    FontAwesomeModule
],
    providers: [],
    templateUrl: './cerrix-tree-select.component.html',
    styleUrl: './cerrix-tree-select.component.scss',
    host: { class: 'cerrix-tree-select' }
})
export class CerrixTreeSelectComponent implements OnInit, ControlValueAccessor {
  protected cascadeChildren = false;

  private readonly treeService = inject(BaseTreeService, {
    optional: true,
  });

  @Input() public type: 'primary' | 'success' = 'primary';
  @Input() public placeholder: string = '';
  @Input() public dataUiid: string = "undefined"

  @Input() public multiple: boolean = false;

  private _treeList: TreeItemNode[] = [];
  public get treeList(): TreeItemNode[] {
    return this._treeList;
  }
  @Input() public set treeList(value: TreeItemNode[]) {
    this._treeList = value;
    this.setSelection();
    this.dataSource.data = this.mapArrayToTree(this.selection);
  }

  _value: string | string[] | undefined;
  @Input()
  set value(value: string | string[] | undefined) {
    if (this._value === value) {
      return;
    }

    this._value = value;
    this.setSelection();
    this.dataSource.data = this.mapArrayToTree(this.selection);
  }
  get value() {
    return this._value;
  }

  @Input() disabled: boolean = false;
  protected ngControl = inject(NgControl, { optional: true, self: true });

  private onChange = (value: string | string[] | undefined) => {};
  private onTouched = () => {};

  protected selection: TreeItemNode[] = [];
  protected singleSelection: TreeItemNode | undefined;

  private readonly dialog = inject(MatDialog);
  private dialogRef!: MatDialogRef<any, any>;
  @ViewChild('modalTemplate') protected modalTemplate!: TemplateRef<any>;
  @ViewChild(CerrixTreeComponent) protected treeSelectRef!: CerrixTreeComponent;

  private readonly _transformer = (node: TreeNestedNode, level: number) => {
    node.item.hasChildren = !!node.children && node.children.length > 0;
    node.item.level = level;

    return node.item;
  };

  protected treeControl = new FlatTreeControl<TreeItemFlatNode>(
    node => node.level,
    node => node.hasChildren
  );

  private readonly treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.hasChildren,
    node => node.children
  );

  protected dataSource = new MatTreeFlatDataSource(
    this.treeControl,
    this.treeFlattener
  );

  protected hasChild = (_: number, nodeData: TreeItemFlatNode) =>
    nodeData.hasChildren;

  private mapArrayToTree(dataset: TreeItemNode[]): TreeNestedNode[] {
    const nestedTree: TreeNestedNode[] = [];

    if (dataset) {
      const hashTable = Object.create(null);
      dataset.forEach(
        item => (hashTable[item.id] = { item: { ...item }, children: [] })
      );

      dataset.forEach(item => {
        if (item.parentId && hashTable[item.parentId])
          hashTable[item.parentId].children.push(hashTable[item.id]);
        else nestedTree.push(hashTable[item.id]);
      });
    }

    return nestedTree;
  }

  writeValue(value: string | string[]): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | string[] | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.treeService) {
      this.cascadeChildren = this.treeService.getCascadeChildren();
    }
  }

  protected onBlur() {
    this.onTouched();
  }

  protected openDialog(): void {
    if (!this.disabled) {
      if (this.treeService) {
        this.cascadeChildren = this.treeService.getCascadeChildren();
      }

      this.onTouched();

      this.dialogRef = this.dialog.open(this.modalTemplate, {
        height: '75%',
        width: '50%',
      });
    }
  }

  protected onApply() {
    if (this.treeSelectRef) {
      const selectedNodes = this.treeSelectRef.getSelectedNodes();
      if (this.multiple) {
        this.value = selectedNodes.map(n => n.id);
      } else {
        this.value = selectedNodes?.length ? selectedNodes[0].id : undefined;
      }

      this.onChange(this.value);
    }

    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  protected removeSelection(node: TreeItemNode) {
    this.onTouched();

    if (this.value) {
      if (this.multiple) {
        this.value = (<string[]>this.value).filter(id => id !== node.id);
      } else {
        this.value = undefined;
      }

      this.onChange(this.value);
    }
  }

  protected toggleCascadeChildren(cascadeChildren: boolean) {
    this.cascadeChildren = cascadeChildren;
    this.treeService?.setCascadeChildren(cascadeChildren);
  }

  private setSelection() {
    if (this.value) {
      if (this.treeList?.length) {
        const values = <string[]>this.value;

        if (this.multiple) {
          this.selection = this.treeList.filter(n => values.includes(n.id));
        } else {
          this.selection = this.treeList.filter(n => this.value == n.id);
        }
      }
    } else {
      this.selection = [];
    }
  }

  protected getIcon(node: TreeItemNode): string {
    if (node.icon) {
      try {
        const icon = JSON.parse(node.icon);
        if (icon) {
          return icon.selector;
        }
      } catch (e) {
        return node.icon;
      }
    }
    return '';
  }
}
